'use client'

import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { Loader2, X } from 'lucide-react'

/** Mirrors prisma AccountType */
export const COMMUNITY_INVITE_ACCOUNT_TYPES: { value: string; label: string }[] = [
  { value: 'CUSTOMER', label: 'Customer' },
  { value: 'PROSPECT', label: 'Prospect' },
  { value: 'PARTNER', label: 'Partner' },
  { value: 'VENDOR', label: 'Vendor' },
  { value: 'RESELLER', label: 'Reseller' },
  { value: 'OTHER', label: 'Other' },
]

export type CommunityInviteSelectedAccount = { id: string; name: string }

type CommunityInviteFilterPanelProps = {
  roomId: string
  selectedAccountTypes: string[]
  onToggleAccountType: (value: string, checked: boolean) => void
  selectedAccounts: CommunityInviteSelectedAccount[]
  onAddAccount: (account: CommunityInviteSelectedAccount) => void
  onRemoveAccount: (accountId: string) => void
  titleContains: string
  onTitleContainsChange: (value: string) => void
  includeContactsWithoutAccount: boolean
  onIncludeContactsWithoutAccountChange: (checked: boolean) => void
  includeInternalUsers: boolean
  onIncludeInternalUsersChange: (checked: boolean) => void
  onApply: () => void
  isLoading?: boolean
  className?: string
}

export function CommunityInviteFilterPanel({
  roomId,
  selectedAccountTypes,
  onToggleAccountType,
  selectedAccounts,
  onAddAccount,
  onRemoveAccount,
  titleContains,
  onTitleContainsChange,
  includeContactsWithoutAccount,
  onIncludeContactsWithoutAccountChange,
  includeInternalUsers,
  onIncludeInternalUsersChange,
  onApply,
  isLoading,
  className,
}: CommunityInviteFilterPanelProps) {
  const [accountSearch, setAccountSearch] = useState('')
  const [accountHits, setAccountHits] = useState<CommunityInviteSelectedAccount[]>([])
  const [accountSearchLoading, setAccountSearchLoading] = useState(false)

  useEffect(() => {
    const q = accountSearch.trim()
    if (q.length < 1) {
      setAccountHits([])
      setAccountSearchLoading(false)
      return
    }
    let cancelled = false
    const id = setTimeout(() => {
      setAccountSearchLoading(true)
      void (async () => {
        try {
          const params = new URLSearchParams({
            query: q,
            roomId,
          })
          const res = await fetch(`/api/accounts/search?${params.toString()}`)
          if (!res.ok || cancelled) return
          const data = (await res.json()) as { id: string; name: string }[]
          const selectedIds = new Set(selectedAccounts.map((a) => a.id))
          if (!cancelled) {
            setAccountHits(data.filter((a) => !selectedIds.has(a.id)).slice(0, 8))
          }
        } catch {
          if (!cancelled) setAccountHits([])
        } finally {
          if (!cancelled) setAccountSearchLoading(false)
        }
      })()
    }, 280)
    return () => {
      cancelled = true
      clearTimeout(id)
    }
  }, [accountSearch, selectedAccounts])

  return (
    <div className={cn('rounded-xl border border-slate-200 bg-slate-50/50 p-4 space-y-4', className)}>
      <div className="space-y-2">
        <p className="text-sm font-medium text-slate-900">Specific accounts</p>
        <p className="text-xs text-slate-500">
          Search by company name to include only contacts linked to those accounts
        </p>
        <div className="relative">
          <Input
            placeholder="Search accounts…"
            value={accountSearch}
            onChange={(e) => setAccountSearch(e.target.value)}
            autoComplete="off"
          />
          {(accountSearchLoading || accountHits.length > 0) && accountSearch.trim().length > 0 && (
            <ul
              className="absolute left-0 right-0 top-full z-20 mt-1 max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-white py-1 text-sm shadow-md"
              role="listbox"
            >
              {accountSearchLoading && accountHits.length === 0 ? (
                <li className="px-3 py-2 text-slate-500">Searching…</li>
              ) : (
                accountHits.map((a) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-left hover:bg-slate-100"
                      onClick={() => {
                        onAddAccount(a)
                        setAccountSearch('')
                        setAccountHits([])
                      }}
                    >
                      {a.name}
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
        {selectedAccounts.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {selectedAccounts.map((a) => (
              <span
                key={a.id}
                className="inline-flex max-w-full items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-800"
              >
                <span className="truncate">{a.name}</span>
                <button
                  type="button"
                  className="rounded-full p-0.5 hover:bg-slate-100"
                  onClick={() => onRemoveAccount(a.id)}
                  aria-label={`Remove ${a.name}`}
                >
                  <X className="h-3 w-3 shrink-0" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <p className="text-sm font-medium text-slate-900">Account type</p>
        <p className="text-xs text-slate-500 mb-2">Include contacts whose account matches any selected type</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {COMMUNITY_INVITE_ACCOUNT_TYPES.map(({ value, label }) => (
            <label
              key={value}
              className="flex items-center gap-2 rounded-lg border border-transparent bg-white px-2 py-1.5 text-sm hover:border-slate-200 cursor-pointer"
            >
              <Checkbox
                checked={selectedAccountTypes.includes(value)}
                onCheckedChange={(c) => onToggleAccountType(value, c === true)}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="community-invite-title-filter">Title contains</Label>
        <Input
          id="community-invite-title-filter"
          placeholder="e.g. VP, Director, Chief"
          value={titleContains}
          onChange={(e) => onTitleContainsChange(e.target.value)}
        />
        <p className="text-xs text-slate-500">Optional. Matches job title (case-insensitive)</p>
      </div>

      <div className="space-y-3">
        <div className="flex items-start gap-2">
          <Checkbox
            id="community-invite-no-account"
            checked={includeContactsWithoutAccount}
            onCheckedChange={(c) => onIncludeContactsWithoutAccountChange(c === true)}
          />
          <div>
            <Label htmlFor="community-invite-no-account" className="text-sm font-normal cursor-pointer">
              Include contacts with no account
            </Label>
            <p className="text-xs text-slate-500">Useful for leads not yet tied to an account</p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <Checkbox
            id="community-invite-internal"
            checked={includeInternalUsers}
            onCheckedChange={(c) => onIncludeInternalUsersChange(c === true)}
          />
          <div>
            <Label htmlFor="community-invite-internal" className="text-sm font-normal cursor-pointer">
              Include internal teammates
            </Label>
            <p className="text-xs text-slate-500">Adds people in your workspace so you can invite them too</p>
          </div>
        </div>
      </div>

      <Button type="button" className="w-full sm:w-auto" onClick={onApply} disabled={isLoading}>
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
            Finding people…
          </>
        ) : (
          'Apply filters'
        )}
      </Button>
    </div>
  )
}
